import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';

import { EnumUserType, useSearchCustomersQuery } from 'lib/graphql';

export interface SearchableCustomer {
  _id: string;
  fullName: string;
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  email?: string | null;
}

export function formatCustomerLabel(c: {
  _id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  company?: string | null;
}): string {
  const name = `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
  return c.company
    ? `${c.company} (${name || c.email || c._id})`
    : name || c.email || c._id;
}

export interface UseCustomerSearchArgs {
  vendorId?: string;
  relationshipType: EnumUserType;
  producerId?: string;
  enabled?: boolean;
}

export interface UseCustomerSearchResult {
  options: SearchableCustomer[];
  loading: boolean;
  input: string;
  onInputChange: (value: string) => void;
  reset: () => void;
}

export function useCustomerSearch({
  vendorId,
  relationshipType,
  producerId,
  enabled = true,
}: UseCustomerSearchArgs): UseCustomerSearchResult {
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');

  const debouncedSetSearch = useMemo(
    () => debounce((v: string) => setSearch(v), 300),
    [],
  );
  useEffect(
    () => () => {
      debouncedSetSearch.cancel();
    },
    [debouncedSetSearch],
  );

  const { data, loading } = useSearchCustomersQuery({
    variables: {
      vendorId: vendorId as string,
      relationshipType,
      search: search || undefined,
      producerId,
    },
    skip: !vendorId || !enabled,
    fetchPolicy: 'cache-and-network',
  });

  const options = useMemo<SearchableCustomer[]>(
    () =>
      (data?.searchCustomers ?? []).map((c) => {
        const id = String(c._id);
        return {
          _id: id,
          fullName: formatCustomerLabel({ ...c, _id: id }),
          first_name: c.first_name,
          last_name: c.last_name,
          company: c.company,
          email: c.email,
        };
      }),
    [data],
  );

  return {
    options,
    loading,
    input,
    onInputChange: (value: string) => {
      setInput(value);
      debouncedSetSearch(value);
    },
    reset: () => {
      setInput('');
      setSearch('');
      debouncedSetSearch.cancel();
    },
  };
}
